import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, Store, Phone, Mail, MapPin, Search, ArrowLeft, Trash2, Calendar, Heart, Clock, Star } from 'lucide-react';
import axios from 'axios';
import { useI18n } from '../contexts/I18nContext';
import { resolveImageUrl } from '../lib/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// B2C Cart Context (shop-specific)
const B2CCartContext = createContext();

export const useB2CCart = () => {
  const context = useContext(B2CCartContext);
  if (!context) {
    throw new Error('useB2CCart must be used within B2CCartProvider');
  }
  return context;
};

export const B2CCartProvider = ({ shopSlug, children }) => {
  const { t } = useI18n();
  const storageKey = `b2c_cart_${shopSlug}`;
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(items));
  }, [items, storageKey]);

  const addItem = (product, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        return prev.map(i => 
          i.product_id === product.id 
            ? {...i, quantity: i.quantity + quantity}
            : i
        );
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: product.price,
        image_url: product.image_url,
        quantity
      }];
    });
    toast.success(t('psAddedToCart'));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) {
      removeItem(productId);
      return;
    }
    setItems(prev => prev.map(i => 
      i.product_id === productId ? {...i, quantity} : i
    ));
  };

  const removeItem = (productId) => {
    setItems(prev => prev.filter(i => i.product_id !== productId));
  };

  const clearCart = () => {
    setItems([]);
  };

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + (i.price * i.quantity), 0);
  const shipping = subtotal >= 200 ? 0 : 6.90;
  const total = subtotal + shipping;

  return (
    <B2CCartContext.Provider value={{
      items, addItem, updateQuantity, removeItem, clearCart,
      itemCount, subtotal, shipping, total
    }}>
      {children}
    </B2CCartContext.Provider>
  );
};

// Public Language Switcher (for shop header)
const PublicLanguageSwitcher = () => {
  const { lang, setLang, LANGS } = useI18n();
  const [open, setOpen] = useState(false);
  const current = LANGS.find(l => l.code === lang) || LANGS[0];
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-sm px-2 py-1 hover:bg-gray-100 rounded"
        data-testid="public-language-selector"
      >
        <span>{current.flag}</span>
        <span className="hidden sm:inline">{current.label}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 bg-white border rounded shadow-lg z-50 min-w-[130px]">
          {LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 ${lang === l.code ? 'bg-gray-100 font-semibold' : ''}`}
              data-testid={`public-lang-${l.code}`}
            >
              <span>{l.flag}</span>
              <span>{l.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Public Shop Layout
const PublicShopLayout = ({ shop, children }) => {
  const { t } = useI18n();
  const { shopSlug } = useParams();
  const { itemCount } = useB2CCart();
  
  // Check if customer is logged in
  const [customer, setCustomer] = useState(null);
  
  useEffect(() => {
    const storedCustomer = localStorage.getItem(`b2c_customer_${shopSlug}`);
    if (storedCustomer) {
      try {
        setCustomer(JSON.parse(storedCustomer));
      } catch (e) {
        localStorage.removeItem(`b2c_customer_${shopSlug}`);
      }
    }
  }, [shopSlug]);
  
  const handleLogout = () => {
    localStorage.removeItem(`b2c_token_${shopSlug}`);
    localStorage.removeItem(`b2c_customer_${shopSlug}`);
    setCustomer(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        {/* Banner */}
        {shop?.banner_url && (
          <div className="h-32 bg-cover bg-center" style={{
            backgroundImage: `url(${API_URL}/api/files/${shop.banner_url})`
          }} />
        )}
        
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to={`/shop/${shopSlug}`} className="flex items-center gap-3">
              {shop?.logo_url ? (
                <img 
                  src={`${API_URL}/api/files/${shop.logo_url}`} 
                  alt={shop.shop_name} 
                  className="h-12 w-12 object-contain"
                />
              ) : (
                <Store className="h-10 w-10 text-primary" />
              )}
              <div>
                <h1 className="text-xl font-bold">{shop?.shop_name || 'Shop'}</h1>
                {shop?.description && (
                  <p className="text-sm text-gray-500 hidden md:block">{shop.description}</p>
                )}
              </div>
            </Link>
            
            <div className="flex items-center gap-2">
              {/* Language Switcher */}
              <PublicLanguageSwitcher />
              {/* Customer Menu */}
              {customer ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 hidden sm:block">
                    Hallo, {customer.first_name}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1"
                    data-testid="logout-btn"
                  >
                    Abmelden
                  </button>
                </div>
              ) : (
                <Link 
                  to={`/shop/${shopSlug}/login`}
                  className="text-sm font-medium hover:underline"
                  data-testid="login-link"
                >
                  {t('psLogin')}
                </Link>
              )}
              
              <Link 
                to={`/shop/${shopSlug}/booking`}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                data-testid="booking-link"
                title={t('psBookAppointment')}
              >
                <Calendar className="h-6 w-6" />
              </Link>
              
              <Link 
                to={`/shop/${shopSlug}/cart`}
                className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
                data-testid="cart-link"
              >
                <ShoppingCart className="h-6 w-6" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">{shop?.shop_name}</h3>
              {shop?.description && <p className="text-gray-400 mb-4">{shop.description}</p>}
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">{t('contact')}</h3>
              <div className="space-y-2 text-gray-400">
                {shop?.contact_name && (
                  <div className="flex items-center gap-2">
                    <Store className="h-4 w-4" />
                    {shop.contact_name}
                  </div>
                )}
                {shop?.contact_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <a href={`tel:${shop.contact_phone}`} className="hover:text-white">{shop.contact_phone}</a>
                  </div>
                )}
                {shop?.contact_email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${shop.contact_email}`} className="hover:text-white">{shop.contact_email}</a>
                  </div>
                )}
                {shop?.street && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {shop.street}, {shop.postal_code} {shop.city}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} {shop?.shop_name}. {t('psAllPricesVat')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Public Shop Home Page
const PublicShopHomePage = () => {
  const { shopSlug } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [customer, setCustomer] = useState(null);
  const { addItem } = useB2CCart();
  const { lang, t } = useI18n();

  useEffect(() => {
    fetchShopData();
  }, [shopSlug, lang]);

  useEffect(() => {
    // Check login and load favorites
    const storedCustomer = localStorage.getItem(`b2c_customer_${shopSlug}`);
    const token = localStorage.getItem(`b2c_token_${shopSlug}`);
    if (storedCustomer && token) {
      try {
        setCustomer(JSON.parse(storedCustomer));
        fetchFavorites(token);
      } catch (e) {
        localStorage.removeItem(`b2c_customer_${shopSlug}`);
      }
    }
  }, [shopSlug]);

  const fetchFavorites = async (token) => {
    try {
      const res = await axios.get(`${API_URL}/api/public/shop/${shopSlug}/customer/favorites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFavorites(res.data || []);
    } catch (e) {
      // Silently ignore — favorites feature is optional
    }
  };

  const fetchShopData = async () => {
    try {
      const localeQuery = lang && lang !== 'de' ? `?locale=${lang}` : '';
      const [shopRes, productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/public/shop/${shopSlug}`),
        axios.get(`${API_URL}/api/public/shop/${shopSlug}/products${localeQuery}`),
        axios.get(`${API_URL}/api/public/shop/${shopSlug}/categories`)
      ]);
      setShop(shopRes.data);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      if (error.response?.status === 404) {
        setError('Shop nicht gefunden');
      } else {
        setError('Fehler beim Laden');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(price);
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategoryId || p.category_id === selectedCategoryId;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Store className="h-16 w-16 text-gray-300 mb-4" />
        <h1 className="text-2xl font-bold mb-2">{error}</h1>
        <p className="text-gray-500 mb-4">Der gesuchte Shop existiert nicht oder ist nicht aktiv.</p>
        <Button onClick={() => navigate('/')}>{t('toHome')}</Button>
      </div>
    );
  }

  return (
    <PublicShopLayout shop={shop}>
      {/* Booking Hero Section */}
      <div className="mb-8 bg-gradient-to-br from-primary via-primary/95 to-primary/80 rounded-2xl overflow-hidden shadow-lg" data-testid="booking-hero">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6 md:p-10">
          <div className="text-primary-foreground space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 text-sm font-medium">
              <Calendar className="h-4 w-4" />
              {t('psOnlineAppointment')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold leading-tight">
              {t('psBookAt', { name: shop.shop_name })}
            </h2>
            <p className="text-primary-foreground/90 text-lg">
              {t('psHeroCopy')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => navigate(`/shop/${shopSlug}/booking`)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-primary rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-md"
                data-testid="hero-book-appointment-btn"
              >
                <Calendar className="h-5 w-5" />
                {t('psBookNow')}
              </button>
              <div className="flex items-center gap-2 text-primary-foreground/80 text-sm">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                <span>{t('psAvailableSlots')}</span>
              </div>
            </div>
          </div>
          <div className="hidden md:flex items-center justify-center relative">
            <div className="grid grid-cols-3 gap-3 w-full max-w-sm">
              {[
                { icon: Calendar, label: t('psStepChoose') },
                { icon: Search, label: t('psStepStylist') },
                { icon: ShoppingCart, label: t('psStepConfirm') },
              ].map((item, i) => (
                <div key={i} className="bg-white/15 backdrop-blur rounded-xl p-4 text-center text-primary-foreground">
                  <item.icon className="h-6 w-6 mx-auto mb-2" />
                  <p className="text-xs whitespace-pre-line font-medium">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Favorites Section – only visible when customer logged in and has favorites */}
      {customer && favorites.length > 0 && (
        <div className="mb-8" data-testid="favorites-section">
          <div className="flex items-center gap-2 mb-4">
            <Heart className="h-5 w-5 text-red-500 fill-red-500" />
            <h2 className="text-2xl font-bold">Deine Favoriten</h2>
            <span className="text-sm text-gray-500 ml-2">
              1-Klick Rebuchung
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {favorites.map(fav => (
              <div
                key={fav.id}
                className="bg-white border border-border rounded-xl p-5 hover:shadow-md transition-shadow flex flex-col"
                data-testid={`favorite-card-${fav.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    <h3 className="font-semibold text-lg">{fav.name}</h3>
                  </div>
                </div>
                {fav.description && (
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">{fav.description}</p>
                )}
                <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {fav.duration_minutes} Min
                  </span>
                  <span className="font-bold text-primary">
                    {formatPrice(fav.price_gross ?? fav.price ?? 0)}
                  </span>
                </div>
                {fav.last_booked && (
                  <p className="text-xs text-gray-400 mb-3">
                    Zuletzt: {fav.last_booked.date} um {fav.last_booked.time} Uhr
                  </p>
                )}
                <Button
                  className="mt-auto w-full"
                  onClick={() => navigate(`/shop/${shopSlug}/booking?service=${fav.id}`)}
                  data-testid={`rebook-favorite-${fav.id}`}
                >
                  <Calendar className="h-4 w-4 me-2" />
                  {t('psBookAgain')}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('searchProduct')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Category Chips */}
      {categories.length > 0 && (
        <div className="mb-6 flex gap-2 overflow-x-auto pb-2" data-testid="category-chips">
          <button
            onClick={() => setSelectedCategoryId(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              !selectedCategoryId 
                ? 'bg-primary text-white' 
                : 'bg-white border border-border text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="category-chip-all"
          >
            {t('psAll')} ({products.length})
          </button>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategoryId(cat.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategoryId === cat.id 
                  ? 'bg-primary text-white' 
                  : 'bg-white border border-border text-gray-600 hover:bg-gray-100'
              }`}
              data-testid={`category-chip-${cat.slug}`}
            >
              {cat.name} ({cat.product_count})
            </button>
          ))}
        </div>
      )}

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12">
          <Store className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold mb-2">{t('psNoProducts')}</h2>
          <p className="text-gray-500">
            {searchTerm ? 'Keine Produkte gefunden' : 'Dieser Shop hat noch keine Produkte freigeschaltet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredProducts.map(product => (
            <div 
              key={product.id} 
              className="bg-white border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              data-testid={`product-card-${product.id}`}
            >
              <Link to={`/shop/${shopSlug}/product/${product.id}`}>
                {product.image_url ? (
                  <img 
                    src={resolveImageUrl(product.image_url)} 
                    alt={product.name}
                    className="w-full h-48 object-contain p-4 bg-white"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                    <Store className="h-12 w-12 text-gray-300" />
                  </div>
                )}
              </Link>
              <div className="p-4">
                <Link to={`/shop/${shopSlug}/product/${product.id}`}>
                  <h3 className="font-semibold mb-1 hover:text-primary transition-colors">{product.name}</h3>
                </Link>
                {product.description && (
                  <p className="text-sm text-gray-500 mb-3 line-clamp-2">{product.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-primary">{formatPrice(product.price)}</span>
                  <Button 
                    size="sm" 
                    onClick={() => addItem(product)}
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    <ShoppingCart className="h-4 w-4 me-1" />
                    {t('addToCart')}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </PublicShopLayout>
  );
};

// Product Detail Page
export const PublicProductPage = () => {
  const { shopSlug, productId } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addItem } = useB2CCart();
  const { lang, t } = useI18n();

  useEffect(() => {
    fetchData();
  }, [shopSlug, productId, lang]);

  const fetchData = async () => {
    try {
      const localeQuery = lang && lang !== 'de' ? `?locale=${lang}` : '';
      const [shopRes, productRes] = await Promise.all([
        axios.get(`${API_URL}/api/public/shop/${shopSlug}`),
        axios.get(`${API_URL}/api/public/shop/${shopSlug}/products/${productId}${localeQuery}`)
      ]);
      setShop(shopRes.data);
      setProduct(productRes.data);
    } catch (error) {
      toast.error('Produkt nicht gefunden');
      navigate(`/shop/${shopSlug}`);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PublicShopLayout shop={shop}>
      <Button variant="ghost" onClick={() => navigate(`/shop/${shopSlug}`)} className="mb-4">
        <ArrowLeft className="h-4 w-4 me-1" />
        {t('psBackToShop')}
      </Button>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6">
          {/* Image */}
          <div>
            {product?.image_url ? (
              <img 
                src={product.image_url} 
                alt={product.name}
                className="w-full rounded-lg"
              />
            ) : (
              <div className="w-full aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                <Store className="h-24 w-24 text-gray-300" />
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="text-2xl font-bold mb-2">{product?.name}</h1>
            <p className="text-3xl font-bold text-primary mb-4">{formatPrice(product?.price || 0)}</p>
            
            {product?.description && (
              <p className="text-gray-600 mb-6">{product.description}</p>
            )}

            {/* Quantity */}
            <div className="flex items-center gap-4 mb-6">
              <span className="font-medium">Menge:</span>
              <div className="flex items-center border rounded">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-2 hover:bg-gray-100"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="px-4 py-2 font-medium">{quantity}</span>
                <button 
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-2 hover:bg-gray-100"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <Button 
              size="lg" 
              className="w-full"
              onClick={() => {
                addItem(product, quantity);
                navigate(`/shop/${shopSlug}/cart`);
              }}
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart className="h-5 w-5 me-2" />
              In den Warenkorb - {formatPrice((product?.price || 0) * quantity)}
            </Button>

            <p className="text-sm text-gray-500 mt-4">
              {t('psFreeShippingFrom')}
            </p>
          </div>
        </div>
      </div>
    </PublicShopLayout>
  );
};

// Cart Page
export const PublicCartPage = () => {
  const { t } = useI18n();
  const { shopSlug } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const { items, updateQuantity, removeItem, subtotal, shipping, total } = useB2CCart();

  useEffect(() => {
    axios.get(`${API_URL}/api/public/shop/${shopSlug}`)
      .then(res => setShop(res.data))
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [shopSlug]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <PublicShopLayout shop={shop}>
      <Button variant="ghost" onClick={() => navigate(`/shop/${shopSlug}`)} className="mb-4">
        <ArrowLeft className="h-4 w-4 me-1" />
        {t('continueShopping')}
      </Button>

      <h1 className="text-2xl font-bold mb-6">{t('cart')}</h1>

      {items.length === 0 ? (
        <div className="bg-white border border-border rounded-lg p-12 text-center">
          <ShoppingCart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold mb-2">{t('emptyCart')}</h2>
          <p className="text-gray-500 mb-4">{t('psAddProducts')}</p>
          <Button onClick={() => navigate(`/shop/${shopSlug}`)}>Zum Shop</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map(item => (
              <div
                key={item.product_id}
                className="bg-white border border-border rounded-lg p-4 flex gap-4"
                data-testid={`b2c-cart-item-${item.product_id}`}
              >
                {item.image_url ? (
                  <img src={resolveImageUrl(item.image_url)} alt={item.name} className="w-20 h-20 object-contain rounded bg-white" />
                ) : (
                  <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
                    <Store className="h-8 w-8 text-gray-300" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-semibold">{item.name}</h3>
                  <p className="text-primary font-bold">{formatPrice(item.price)}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex items-center border rounded">
                      <button 
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="p-1 hover:bg-gray-100"
                        data-testid={`b2c-decrease-quantity-${item.product_id}`}
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span
                        className="px-3 text-sm"
                        data-testid={`b2c-quantity-${item.product_id}`}
                      >
                        {item.quantity}
                      </span>
                      <button 
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="p-1 hover:bg-gray-100"
                        data-testid={`b2c-increase-quantity-${item.product_id}`}
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <button 
                      onClick={() => removeItem(item.product_id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      data-testid={`b2c-remove-item-${item.product_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="text-end">
                  <p className="font-bold">{formatPrice(item.price * item.quantity)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="bg-white border border-border rounded-lg p-6 h-fit sticky top-24">
            <h2 className="text-lg font-bold mb-4">{t('psSummary')}</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>{t('subtotal')}</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>{t('shipping')}</span>
                <span>{shipping === 0 ? 'Kostenlos' : formatPrice(shipping)}</span>
              </div>
              {subtotal < 200 && (
                <p className="text-xs text-gray-500">
                  {t('psUntilFreeShipping', { amount: formatPrice(200 - subtotal) })}
                </p>
              )}
              <hr className="my-3" />
              <div className="flex justify-between font-bold text-lg">
                <span>{t('total')}</span>
                <span>{formatPrice(total)}</span>
              </div>
              <p className="text-xs text-gray-500">{t('psInclVat')}</p>
            </div>
            <Button 
              className="w-full mt-4" 
              size="lg"
              onClick={() => navigate(`/shop/${shopSlug}/checkout`)}
              data-testid="checkout-btn"
            >
              {t('checkout')}
            </Button>
          </div>
        </div>
      )}
    </PublicShopLayout>
  );
};

export default PublicShopHomePage;
